import * as React from "react";
import { StatusCard } from "@/components/dashboard/StatusCard";
import { QueryStateBoundary } from "@/components/dashboard/QueryStateBoundary";
import { PositionTable, type PositionRow } from "../PositionTable";
import { usePositions } from "@/hooks/queries/usePortfolio";
import { appConfig } from "@/utils/config";

interface PositionPayload {
  instrument_token: string;
  quantity: number;
  average_price: number;
}

/**
 * Backend v2.1 (Sprint 4.2) `GET /api/v1/portfolio/positions` - a
 * genuine read of already-persisted open positions, replacing the
 * Sprint 3.3-era `GET /orders` proxy workaround (`positionService`,
 * removed this sprint). `PositionDto` carries `instrument_token`/
 * `quantity`/`average_price` only - `side`/`entry_time`/`status` remain
 * "Not reported by backend" (Portfolio's own read model has no order-
 * side/entry-time/lifecycle-status concept; that belongs to Order
 * Management, a different bounded context).
 */
export function PositionsSection(): React.JSX.Element {
  const positionsQuery = usePositions(appConfig.defaultPortfolioId);

  return (
    <StatusCard title="Open Positions">
      <QueryStateBoundary
        query={positionsQuery}
        isEmpty={(data) => (data as unknown as PositionPayload[]).length === 0}
        emptyMessage="No open positions reported by the backend."
      >
        {(payload) => {
          const positions = payload as unknown as PositionPayload[];
          const rows: PositionRow[] = positions.map((position) => ({
            execution_id: position.instrument_token,
            instrument_token: position.instrument_token,
            side: null,
            quantity: position.quantity,
            average_price: position.average_price,
            entry_time: null,
            status: null,
          }));
          return <PositionTable positions={rows} />;
        }}
      </QueryStateBoundary>
    </StatusCard>
  );
}
